import React from "react";
import { Formik, Form , Field, ErrorMessage} from "formik";
import ModalComponent from "../../../../components/Modal";
import { projectValidationSchema } from "../../../../components/Helpers";
import CustomDropdown from "../../../../components/CustomDropdown";
import TextInput from "../../../../components/TextInput";
import MuiDatePicker from "../../../../components/MuiDatePicker";
import moment from "moment";

const AddProjectModal = ({
  onClose,
  show,
  onAddProject,
  title,
  initialValues,
  usersList = [],
  clientsList = [],
}) => {
  const isViewMode = title === "View Project";

  const handleSubmit = (values, { resetForm }) => {
    if (isViewMode) return; // Prevent submission in view mode
    onAddProject(values);
    resetForm();
  };

  return (
    <ModalComponent show={show} onClose={onClose} title={title}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={!isViewMode ? projectValidationSchema : null}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[85vh] overflow-y-auto no-scrollbar p-4">
            {/* Project Name */}
            <div className="col-span-1 md:col-span-2">
              <TextInput
                value={values?.projectName || ''}
                name="projectName"
                placeholder="Project Name"
                handleChange={(e) => !isViewMode && handleChange(e)}
                isDisabled={isViewMode}
                classes={`w-full ${isViewMode ? 'cursor-not-allowed' : ''}`}
                legend
                readOnly={isViewMode}
              />
              {!isViewMode && errors.projectName && touched.projectName && (
                <div className="text-red-500 text-sm">{errors.projectName}</div>
              )}
            </div>

            {/* User Id & Client Id */}
            <div className="w-full">
              <CustomDropdown
                value={usersList.find((user) => user.id == values?.userId) || null}
                name="userId"
                handleChange={(selectedOption) => !isViewMode && setFieldValue("userId", selectedOption?.id)}
                customClasses={`w-full text-left ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Select a user"
                options={isViewMode ? [usersList.find((user) => user.id == values?.userId)] : usersList}
                handleGetOptionValue={(option) => option.id}
                handleGetOptionName={(option) => `${option.firstName} ${option.lastName}`}
                isDisabled={isViewMode}
                isSearchable={!isViewMode}
              />
              {!isViewMode && errors.userId && touched.userId && (
                <div className="text-red-500 text-sm">{errors.userId}</div>
              )}
            </div>

            <div className="w-full">
              <CustomDropdown
                value={clientsList.find((client) => client.id == values?.clientId) || null}
                name="clientId"
                handleChange={(selectedOption) => !isViewMode && setFieldValue("clientId", selectedOption?.id)}
                customClasses={`w-full text-left ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Select a client"
                options={isViewMode ? [clientsList.find((client) => client.id == values?.clientId)] : clientsList}
                handleGetOptionValue={(option) => option.id}
                handleGetOptionName={(option) => option.name}
                isDisabled={isViewMode}
                isSearchable={!isViewMode}
              />
              {!isViewMode && errors.clientId && touched.clientId && (
                <div className="text-red-500 text-sm">{errors.clientId}</div>
              )}
            </div>

            {/* Start Date & End Date */}
            <div className="w-full">
              <MuiDatePicker
                label="Start Date"
                value={values?.startDate ? moment(values?.startDate) : null}
                onChange={(date) => !isViewMode && setFieldValue("startDate", moment(date).format("MM-DD-YYYY"))}
                disabled={isViewMode}
                className={`w-full h-10 ${isViewMode ? 'bg-primary cursor-not-allowed' : ''}`}
                maxDate={values?.endDate ? moment(values.endDate) : null}
                readOnly={isViewMode}
              />
              {!isViewMode && errors.startDate && touched.startDate && (
                <div className="text-red-500 text-sm">{errors.startDate}</div>
              )}
            </div>

            <div className="w-full">
              <MuiDatePicker
                label="End Date"
                value={values?.endDate ? moment(values?.endDate) : null}
                onChange={(date) => !isViewMode && setFieldValue("endDate", moment(date).format("MM-DD-YYYY"))}
                disabled={isViewMode}
                className={`w-full h-10 ${isViewMode ? 'bg-primary cursor-not-allowed' : ''}`}
                minDate={values?.startDate ? moment(values.startDate) : null}
                readOnly={isViewMode}
              />
              {!isViewMode && errors.endDate && touched.endDate && (
                <div className="text-red-500 text-sm">{errors.endDate}</div>
              )}
            </div>

            {/* Budget & Net Pay */}
            <div className="w-full">
              <TextInput
                value={values?.budget || ''}
                name="budget"
                placeholder="Budget"
                handleChange={(e) => !isViewMode && handleChange(e)}
                classes={`w-full ${isViewMode ? 'bg-primary cursor-not-allowed' : ''}`}
                legend
                isDisabled={isViewMode}
                readOnly={isViewMode}
              />
              {!isViewMode && errors.budget && touched.budget && (
                <div className="text-red-500 text-sm">{errors.budget}</div>
              )}
            </div>

            <div className="w-full">
              <TextInput
                value={values?.netPay || ''}
                name="netPay"
                placeholder="Net Pay"
                handleChange={(e) => !isViewMode && handleChange(e)}
                classes={`w-full ${isViewMode ? 'bg-primary cursor-not-allowed' : ''}`}
                legend
                isDisabled={isViewMode}
                readOnly={isViewMode}
              />
              {!isViewMode && errors.netPay && touched.netPay && (
                <div className="text-red-500 text-sm">{errors.netPay}</div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row justify-end gap-2 mt-4">
              <button
                type="button"
                className="bg-primary text-black h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-primaryDark"
                onClick={onClose}
              >
                {isViewMode ? "Close" : "Cancel"}
              </button>
              {!isViewMode && (
                <button
                  type="submit"
                  className="bg-primary text-white h-10 py-1 px-4 w-full sm:w-40 rounded-lg hover:bg-primaryDark"
                >
                  {title === "Edit Project" ? "Update" : "Submit"}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </ModalComponent>
  );
};

export default AddProjectModal;
